import {
  includesCompanionPhrase,
  normalizeCompanionTranscript
} from "./companionVoice";

export type VideoCompanionStage =
  | "p1_recommend"
  | "p2_playing"
  | "p3_continue"
  | "p4_quicklook"
  | "p5_actor";

export type VideoCompanionVideoId =
  | "world-cup-live"
  | "crossroads-continue"
  | "grandma-letter-hot"
  | "interstellar-trailer"
  | "hot-comedy-short"
  | "dune-three-quick"
  | "dune-three-feature"
  | "wandering-city-short"
  | "dune-universe-recap"
  | "yolo-quick";

export type VideoCompanionVoiceIntent = "actor" | "quicklook" | "continue" | "reset";

export interface VideoCompanionDemoState {
  active: true;
  selectedVideoId: VideoCompanionVideoId;
  stage: VideoCompanionStage;
}

export interface VideoCompanionVideo {
  id: VideoCompanionVideoId;
  title: string;
  subtitle: string;
  category: string;
  durationLabel: string;
  imageSrc: string;
  description: string;
  tags: string[];
  person: VideoCompanionPersonProfile;
}

export interface VideoCompanionPersonProfile {
  name: string;
  roleLabel: string;
  relation: string;
  imageSrc?: string;
  summary: string;
  facts: string[];
  sourceLabel: string;
  sourceUrl: string;
  imageCredit?: string;
}

export interface VideoCompanionRecommendation {
  videoId: VideoCompanionVideoId;
  badge: string;
  note: string;
  linkLabel?: string;
}

export const VIDEO_COMPANION_CONTEXT = {
  time: "18:40",
  chargingCopy: "预计充电 32 分钟 · 够看一部短的",
  chargingStatus: "超充已连接 · 剩余 32 分钟",
  stationName: "星越超充站 B2",
  batteryLevel: "37%",
  targetLevel: "80%"
} as const;

export const VIDEO_COMPANION_VIDEOS: VideoCompanionVideo[] = [
  {
    id: "world-cup-live",
    title: "世界杯直播·今晚焦点战",
    subtitle: "巴西 VS 日本 比分正焦灼",
    category: "体育直播",
    durationLabel: "直播中",
    imageSrc: "assets/figma/night-ai/video-companion/world-cup-focus.webp",
    description: "焦点战进入关键时段，适合充电等待时快速接入。",
    tags: ["世界杯", "直播", "焦点战"],
    person: {
      name: "内马尔",
      roleLabel: "巴西足球运动员",
      relation: "巴西队焦点球员",
      imageSrc: "assets/figma/night-ai/video-companion/people/person-neymar.webp",
      summary: "内马尔以盘带、创造力和终结能力著称，是巴西国家队历史射手王之一，也是这一类巴西焦点战最容易被关注的人物。",
      facts: ["1992 年出生于巴西", "司职前锋 / 攻击型中场", "巴西国家队代表人物"],
      sourceLabel: "Wikipedia / Wikimedia Commons",
      sourceUrl: "https://en.wikipedia.org/wiki/Neymar",
      imageCredit: "Wikimedia Commons · CC BY-SA 4.0"
    }
  },
  {
    id: "crossroads-continue",
    title: "罗永浩的十字路口·继续看",
    subtitle: "上次看到 35 分 20 秒",
    category: "访谈",
    durationLabel: "续播",
    imageSrc: "assets/figma/night-ai/video-companion/crossroads-continue.webp",
    description: "延续上次观看进度，并沿用你的倍速偏好。",
    tags: ["访谈", "续播", "1.25x"],
    person: {
      name: "罗永浩",
      roleLabel: "企业家 / 主播",
      relation: "节目访谈对象",
      imageSrc: "assets/figma/night-ai/video-companion/people/person-luo-yonghao.webp",
      summary: "罗永浩曾任新东方英语教师，后来创办锤子科技，并进入直播电商领域；这类访谈通常围绕创业、产品和个人转型展开。",
      facts: ["1972 年出生", "锤子科技创始人", "从教育、硬件创业转向直播电商"],
      sourceLabel: "Wikipedia / Wikimedia Commons",
      sourceUrl: "https://en.wikipedia.org/wiki/Luo_Yonghao",
      imageCredit: "Wikimedia Commons · CC BY-SA 4.0"
    }
  },
  {
    id: "grandma-letter-hot",
    title: "给阿嬷的情书·热映中",
    subtitle: "剧情爱情 · 豆瓣 9.3",
    category: "电影",
    durationLabel: "热映",
    imageSrc: "assets/figma/night-ai/video-companion/grandma-letter-hot.webp",
    description: "近期多人观看的温情电影，EVA 会保留充电提醒。",
    tags: ["热映", "剧情", "爱情"],
    person: {
      name: "李思潼",
      roleLabel: "演员",
      relation: "《给阿嬷的情书》核心主演",
      imageSrc: "assets/figma/night-ai/video-companion/people/person-li-sitong.webp",
      summary: "李思潼是《给阿嬷的情书》的核心主演之一，影片围绕亲情记忆、跨代沟通和潮汕地域情感展开。",
      facts: ["出演《给阿嬷的情书》", "影片入围并放映于多个华语电影相关活动", "角色线承担亲情叙事的主要情绪入口"],
      sourceLabel: "中国新闻网 / 影片报道",
      sourceUrl: "https://www.chinaqw.com/hwjy/2026/05-19/406221.shtml",
      imageCredit: "中国新闻网 · 影片剧照"
    }
  },
  {
    id: "interstellar-trailer",
    title: "星际旅途",
    subtitle: "太空科幻精选",
    category: "电影片段",
    durationLabel: "12 分钟",
    imageSrc: "assets/figma/night-ai/video-companion/interstellar-trailer.webp",
    description: "节奏平稳的太空片段，适合充电等待时沉浸观看。",
    tags: ["科幻", "沉浸", "12 min"],
    person: {
      name: "马修·麦康纳",
      roleLabel: "演员",
      relation: "太空科幻片代表主演",
      imageSrc: "assets/figma/night-ai/video-companion/people/person-matthew-mcconaughey.webp",
      summary: "马修·麦康纳是美国演员，曾主演《星际穿越》等作品；在太空科幻内容里常被联想到库珀这类关键角色。",
      facts: ["1969 年出生", "奥斯卡最佳男主角得主", "代表作包括《星际穿越》《达拉斯买家俱乐部》"],
      sourceLabel: "Wikipedia / Wikimedia Commons",
      sourceUrl: "https://en.wikipedia.org/wiki/Matthew_McConaughey",
      imageCredit: "Wikimedia Commons · CC BY-SA 2.0"
    }
  },
  {
    id: "hot-comedy-short",
    title: "热辣滚烫",
    subtitle: "三分钟速看",
    category: "速看版",
    durationLabel: "3 分钟",
    imageSrc: "assets/figma/night-ai/video-companion/hot-comedy-short.webp",
    description: "三分钟补齐剧情高光，轻松、不打断休息。",
    tags: ["喜剧", "口碑", "3 min"],
    person: {
      name: "贾玲",
      roleLabel: "演员 / 喜剧人",
      relation: "《热辣滚烫》核心主演",
      imageSrc: "assets/figma/night-ai/video-companion/people/person-jia-ling.webp",
      summary: "贾玲是中国喜剧演员、导演，也是《热辣滚烫》的核心主演；这类速看内容会重点围绕她的角色转变和情绪线展开。",
      facts: ["1982 年出生", "大碗娱乐联合创始人", "主演《你好，李焕英》《热辣滚烫》"],
      sourceLabel: "Wikipedia / 新浪影像",
      sourceUrl: "https://en.wikipedia.org/wiki/Jia_Ling",
      imageCredit: "新浪 · 影片剧照"
    }
  },
  {
    id: "dune-three-quick",
    title: "沙丘3",
    subtitle: "四分钟速看",
    category: "速看版",
    durationLabel: "4 分钟",
    imageSrc: "assets/figma/night-ai/video-companion/dune-three-quick.webp",
    description: "快速回顾角色关系与关键冲突，充电结束前也能看完。",
    tags: ["新上线", "科幻", "4 min"],
    person: {
      name: "提莫西·查拉梅",
      roleLabel: "演员",
      relation: "《沙丘》系列保罗·厄崔迪饰演者",
      imageSrc: "assets/figma/night-ai/video-companion/people/person-timothee-chalamet.webp",
      summary: "提莫西·查拉梅是美国、法国演员，在《沙丘》系列中饰演保罗·厄崔迪，是理解沙丘人物线的核心角色。",
      facts: ["1995 年出生", "主演《沙丘》《请以你的名字呼唤我》", "多次获得奥斯卡、BAFTA 等提名"],
      sourceLabel: "Wikipedia / Wikimedia Commons",
      sourceUrl: "https://en.wikipedia.org/wiki/Timoth%C3%A9e_Chalamet",
      imageCredit: "Wikimedia Commons · CC BY-SA 4.0"
    }
  },
  {
    id: "dune-three-feature",
    title: "沙丘3",
    subtitle: "正片续看",
    category: "电影",
    durationLabel: "42 分钟",
    imageSrc: "assets/figma/night-ai/video-companion/dune-three-feature.webp",
    description: "从当前角色线继续播放，EVA 保留充电倒计时提醒。",
    tags: ["正片", "续看", "42 min"],
    person: {
      name: "提莫西·查拉梅",
      roleLabel: "演员",
      relation: "《沙丘》系列保罗·厄崔迪饰演者",
      imageSrc: "assets/figma/night-ai/video-companion/people/person-timothee-chalamet.webp",
      summary: "提莫西·查拉梅是美国、法国演员，在《沙丘》系列中饰演保罗·厄崔迪，是理解沙丘人物线的核心角色。",
      facts: ["1995 年出生", "主演《沙丘》《请以你的名字呼唤我》", "多次获得奥斯卡、BAFTA 等提名"],
      sourceLabel: "Wikipedia / Wikimedia Commons",
      sourceUrl: "https://en.wikipedia.org/wiki/Timoth%C3%A9e_Chalamet",
      imageCredit: "Wikimedia Commons · CC BY-SA 4.0"
    }
  },
  {
    id: "wandering-city-short",
    title: "流浪城市",
    subtitle: "三分钟速看",
    category: "速看版",
    durationLabel: "3 分钟",
    imageSrc: "assets/figma/night-ai/video-companion/wandering-city-short.webp",
    description: "同类科幻短内容，剧情密度高，适合短等待。",
    tags: ["科幻", "城市", "3 min"],
    person: {
      name: "吴京",
      roleLabel: "演员",
      relation: "科幻电影核心主演",
      imageSrc: "assets/figma/night-ai/video-companion/people/person-wu-jing.webp",
      summary: "吴京是中国演员、导演，在《流浪地球》系列中出演关键角色；这类科幻速看内容会优先介绍他的角色动机和剧情作用。",
      facts: ["主演《流浪地球》系列", "代表作包括《战狼2》《长津湖》", "动作片和工业化科幻片的代表演员之一"],
      sourceLabel: "Wikipedia / 文汇影像",
      sourceUrl: "https://en.wikipedia.org/wiki/Wu_Jing_(actor)",
      imageCredit: "文汇 · 影片剧照"
    }
  },
  {
    id: "dune-universe-recap",
    title: "沙丘宇宙",
    subtitle: "人物关系回顾",
    category: "补课内容",
    durationLabel: "6 分钟",
    imageSrc: "assets/figma/night-ai/video-companion/dune-universe-recap.webp",
    description: "把家族、星球和角色线重新串起来，看正片前更容易进入。",
    tags: ["人物", "设定", "6 min"],
    person: {
      name: "提莫西·查拉梅",
      roleLabel: "演员",
      relation: "《沙丘》系列保罗·厄崔迪饰演者",
      imageSrc: "assets/figma/night-ai/video-companion/people/person-timothee-chalamet.webp",
      summary: "提莫西·查拉梅是美国、法国演员，在《沙丘》系列中饰演保罗·厄崔迪，是理解沙丘人物线的核心角色。",
      facts: ["1995 年出生", "主演《沙丘》《请以你的名字呼唤我》", "多次获得奥斯卡、BAFTA 等提名"],
      sourceLabel: "Wikipedia / Wikimedia Commons",
      sourceUrl: "https://en.wikipedia.org/wiki/Timoth%C3%A9e_Chalamet",
      imageCredit: "Wikimedia Commons · CC BY-SA 4.0"
    }
  },
  {
    id: "yolo-quick",
    title: "热辣滚烫",
    subtitle: "剧情高光速看",
    category: "速看版",
    durationLabel: "3 分钟",
    imageSrc: "assets/figma/night-ai/video-companion/yolo-quick.webp",
    description: "刚出炉的热门速看版，用三分钟抓住剧情高光。",
    tags: ["口碑炸裂", "热门", "3 min"],
    person: {
      name: "贾玲",
      roleLabel: "演员 / 喜剧人",
      relation: "《热辣滚烫》核心主演",
      imageSrc: "assets/figma/night-ai/video-companion/people/person-jia-ling.webp",
      summary: "贾玲是中国喜剧演员、导演，也是《热辣滚烫》的核心主演；这类速看内容会重点围绕她的角色转变和情绪线展开。",
      facts: ["1982 年出生", "大碗娱乐联合创始人", "主演《你好，李焕英》《热辣滚烫》"],
      sourceLabel: "Wikipedia / 新浪影像",
      sourceUrl: "https://en.wikipedia.org/wiki/Jia_Ling",
      imageCredit: "新浪 · 影片剧照"
    }
  }
];

export const VIDEO_COMPANION_P1_RECOMMENDATIONS: VideoCompanionRecommendation[] = [
  { videoId: "world-cup-live", badge: "时效推荐", note: "你最近常看世界杯" },
  { videoId: "crossroads-continue", badge: "续播", note: "1.25 倍速沿用" },
  { videoId: "grandma-letter-hot", badge: "热门", note: "近期多人观看" }
];

export const VIDEO_COMPANION_CONTINUE_RECOMMENDATIONS: VideoCompanionRecommendation[] = [
  { videoId: "dune-three-feature", badge: "片 1", note: "继续看正片", linkLabel: "3分钟速看" },
  { videoId: "wandering-city-short", badge: "片 2", note: "同类科幻短内容", linkLabel: "3分钟速看" },
  { videoId: "dune-universe-recap", badge: "片 3", note: "补齐人物关系", linkLabel: "3分钟速看" }
];

export const VIDEO_COMPANION_QUICKLOOK_RECOMMENDATIONS: VideoCompanionRecommendation[] = [
  { videoId: "dune-three-quick", badge: "速看", note: "新上线" },
  { videoId: "yolo-quick", badge: "速看", note: "口碑炸裂" }
];

export function createInitialVideoCompanionState(): VideoCompanionDemoState {
  return {
    active: true,
    selectedVideoId: "grandma-letter-hot",
    stage: "p1_recommend"
  };
}

export function getVideoCompanionVideo(videoId: VideoCompanionVideoId) {
  return VIDEO_COMPANION_VIDEOS.find((video) => video.id === videoId) ?? VIDEO_COMPANION_VIDEOS[0];
}

export function matchVideoCompanionVoiceIntent(transcript: string): VideoCompanionVoiceIntent | null {
  const normalizedTranscript = normalizeCompanionTranscript(transcript);

  if (!normalizedTranscript) {
    return null;
  }

  if (includesCompanionPhrase(normalizedTranscript, ["重新开始", "重置", "回到推荐", "回推荐页"])) {
    return "reset";
  }

  if (includesCompanionPhrase(normalizedTranscript, [
    "这人是谁",
    "这个人是谁",
    "这是谁",
    "他是谁",
    "她是谁",
    "演员是谁",
    "主演是谁",
    "主角是谁",
    "他叫什么",
    "她叫什么",
    "介绍一下这个人"
  ])) {
    return "actor";
  }

  if (includesCompanionPhrase(normalizedTranscript, ["片尾", "播完", "结束了", "看完了", "还有时间"])) {
    return "continue";
  }

  if (
    includesCompanionPhrase(normalizedTranscript, ["速看", "推荐", "看什么", "找一下", "帮我找", "类似"]) &&
    includesCompanionPhrase(normalizedTranscript, ["视频", "电影", "内容", "片", "短的", "短视频", "看"])
  ) {
    return "quicklook";
  }

  return null;
}
